import { ErrorHandler, Service } from '@angular/core';
import { addAngularError } from '@datadog/browser-rum-angular';

import { Logger } from './logger.service';
import { attemptChunkReload } from './chunk-recovery';

/**
 * Global Angular ErrorHandler that forwards uncaught errors to the
 * shared Logger (which in turn ships them to Datadog with the Error
 * object intact). Without this, async errors from observables/promises
 * are only caught by datadogLogs's window.onerror hook and lose context.
 * Errors are additionally reported to Datadog RUM (no-op until RUM starts).
 */
@Service({ autoProvided: false })
export class DatadogErrorHandler implements ErrorHandler {
  private readonly log = new Logger('UncaughtError');

  handleError(error: unknown): void {
    // A lazy import() that rejects inside the Angular zone (a lazy route, or a
    // dynamic import in a component's ngOnInit) lands here, not on window
    // 'unhandledrejection', so main.ts's recovery never sees it. After a deploy
    // this is the stale-chunk 404. Try the shared loop-guarded reload FIRST and
    // return before reporting: an error we recover from automatically is not a
    // defect, and reporting it anyway drowns the sessions that really did break.
    if (attemptChunkReload(error)) {
      this.log.warn('Chunk load error caught by ErrorHandler, reloading page...');
      return;
    }

    addAngularError(error);
    if (error instanceof Error) {
      this.log.error(error.message, error);
    } else {
      this.log.error('Non-Error thrown', String(error));
    }
  }
}
